import styled from '@emotion/styled';
import {
  type AllReadsQuery,
  DBTimestampType,
  RacemapAPIClient,
} from '@racemap/utilities/api-client';
import { HAIRSP } from '@racemap/utilities/consts/characters';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { isDefined } from '@racemap/utilities/functions/validation';
import type { RacemapStarters } from '@racemap/utilities/types/events';
import type { Read, ReadWithStarter } from '@racemap/utilities/types/prediction';
import type { Reader } from '@racemap/utilities/types/types';
import { Space, Table, type TableColumnType, type TableProps, Tag } from 'antd';
import type { Immutable } from 'immer';
import pick from 'lodash/pick';
import { DateTime } from 'luxon';
import { type FC, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import useSWR from 'swr';
import { useResizeObserver } from 'usehooks-ts';
import type { ReadsFilter } from '../../store/predictionStore';
import { useStore } from '../../store/reducers';
import { LatLngExternalLink, Timestamp } from '../BasicComponents';
import {
  IconClock,
  IconLocation,
  IconLocationDot,
  IconRacemap,
  IconUnlink,
} from '../BasicComponents/Icons';
import { TooltipForAnObject } from '../BasicComponents/Tooltips/TooltipForAnObject';
import { TooltipWithUTCDatetime } from '../BasicComponents/Tooltips/TooltipWithUTCDatetime';
import { columnTitle, compareDates } from '../utils/tableColumns';
import { TypeOfSourceIcon } from './AnalyseComponents/utils';
import { FetchReadsOptions } from './FetchReadsOptions';
import type { TimeFrames } from './TimeFramesIndicator';
import { TransponderId } from './TransponderId';
import { UserOrCustomerId } from './UserOrCustomerId';

type PreparedReads = Immutable<Array<ReadWithStarter>>;

const apiClient = RacemapAPIClient.fromWindowLocation();

const HEADER_HEIGHT = 39;

function getResultStartTimeStamp(
  reads: PreparedReads,
  timestampFilterType?: DBTimestampType,
): number | null {
  let result: number | null = null;
  if (timestampFilterType === DBTimestampType.ReadAt) {
    result = reads.reduce((acc, read) => {
      if (read.readAt.getTime() < acc) return read.readAt.getTime();
      return acc;
    }, new Date(reads[0]?.readAt).getTime() ?? null);
  }

  if (timestampFilterType === DBTimestampType.ReceivedAt) {
    result = reads.reduce((acc, read) => {
      if (read.receivedAt.getTime() < acc) return read.receivedAt.getTime();
      return acc;
    }, new Date(reads[0]?.receivedAt).getTime() ?? null);
  }

  return result;
}

function getResultEndTimeStamp(
  reads: PreparedReads,
  timestampFilterType?: DBTimestampType,
): number | null {
  let result: number | null = null;
  if (timestampFilterType === DBTimestampType.ReadAt) {
    result = reads.reduce((acc, read) => {
      if (read.readAt.getTime() > acc) return read.readAt.getTime();
      return acc;
    }, reads[0]?.readAt.getTime() ?? null);
  }

  if (timestampFilterType === DBTimestampType.ReceivedAt) {
    result = reads.reduce((acc, read) => {
      if (read.receivedAt.getTime() > acc) return read.receivedAt.getTime();
      return acc;
    }, reads[0]?.receivedAt.getTime() ?? null);
  }

  return result;
}

function linkReadsWithItsCorrespondingStarters(
  rawReads: Array<Read>,
  starters?: Immutable<RacemapStarters>,
): Array<Immutable<ReadWithStarter>> {
  if (!starters) return rawReads.map((read) => ({ ...read, id: read.readAt.toISOString() }));

  const readsWithStarter: Array<ReadWithStarter> = [];
  const startersMap = new Map(Array.from(starters.values()).map((s) => [s.appId, s]));

  for (const read of rawReads) {
    const starter = startersMap.get(read.chipId ?? null);
    readsWithStarter.push({
      ...read,
      id: read.readAt.toISOString(),
      starter,
    });
  }

  return readsWithStarter;
}

export const ReadsSubTab: FC = () => {
  const event = useCurrentEvent();
  const { setReadsFilter } = useStore((s) => s.prediction.actions);
  const readsFilter = useStore((s) => s.prediction.readsFilter);
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const { height: tableAreaHeight = 0 } = useResizeObserver({ ref: tableAreaRef });
  const emptyBodyHeight = Math.max(tableAreaHeight - HEADER_HEIGHT, 0);

  const readersFilter: Immutable<AllReadsQuery> = pick(readsFilter, [
    'eventId',
    'startTime',
    'endTime',
    'timestampFilterType',
    'isTest',
    'userIds',
  ]);

  const predictionIsEnabled = event?.modules.predictive.enabled;
  const { data: readers } = useSWR<Array<Reader>>(
    predictionIsEnabled ? ['Readers', readersFilter] : null,
    async ([, readersFilter]: [string, AllReadsQuery]) => {
      return await apiClient.prediction().getAllReadersForEvent(readersFilter);
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  const { data: readsCount, isLoading: isLoadingReadsCount } = useSWR<number>(
    predictionIsEnabled ? ['ReadsCount', readsFilter] : null,
    async ([, readsFilter]: [string, ReadsFilter]) => {
      const countResult = await apiClient.prediction().getAllReadsByEventCount({
        ...pick(readsFilter, [
          'isTest',
          'eventId',
          'endTime',
          'startTime',
          'timestampFilterType',
          'readerIds',
          'userIds',
          'chipIds',
        ]),
      });
      return countResult.count;
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  const { data: reads, isLoading: isLoadingReads } = useSWR<Array<Read>>(
    predictionIsEnabled ? ['Reads', readsFilter] : null,
    async ([, readsFilter]: [string, ReadsFilter]) => {
      return await apiClient.prediction().getAllReadsByEvent(readsFilter);
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  const linkedReads = linkReadsWithItsCorrespondingStarters(reads ?? [], event?.starters);

  const timeFrames: TimeFrames = {
    eventStart: event?.startTime ? new Date(event?.startTime).getTime() : null,
    eventEnd: event?.endTime ? new Date(event?.endTime).getTime() : null,
    searchWindowStart: readsFilter.startTime.getTime(),
    searchWindowEnd: readsFilter.endTime.getTime(),
    resultWindowStart: getResultStartTimeStamp(linkedReads, readsFilter.timestampFilterType),
    resultWindowEnd: getResultEndTimeStamp(linkedReads, readsFilter.timestampFilterType),
  };

  const availableChipIds: Array<string> =
    event?.starters != null
      ? Array.from(event?.starters.values())
          .map((obj) => obj.appId ?? '')
          .filter((item) => item !== '')
      : [];

  const availableReaderIds: Array<string> = Array.from(
    new Set(readers?.map((r) => r.readerId) ?? []),
  );

  const isReadAtFilterActive = readsFilter.timestampFilterType === DBTimestampType.ReadAt;
  const isReceivedAtFilterActive = readsFilter.timestampFilterType === DBTimestampType.ReceivedAt;

  const columns: TableProps<ReadWithStarter>['columns'] = [
    {
      key: 'index',
      title: '#',
      width: 60,
      align: 'center',
      render: (_, __, index) => <b>{index + 1}</b>,
    },
    sourceColumn(),
    {
      key: 'readAt',
      title: columnTitle({
        label: (
          <span style={isReadAtFilterActive ? { color: RacemapColors.PaleBlue } : undefined}>
            {isReadAtFilterActive ? 'Read At (filter)' : 'Read At'}
          </span>
        ),
        headerTitle:
          'Time when the read was recorded by the related the reader on the raceground. If marked with "(filter)", Begin/Finish is applied to this field.',
      }),
      width: 180,
      align: 'center',
      sorter: (a, b) => compareDates(a.readAt, b.readAt),
      render: (_, read) => (
        <TooltipWithUTCDatetime datetime={read.readAt}>
          <Space>
            <Timestamp time={read.readAt} format="dayWithTime" />
          </Space>
        </TooltipWithUTCDatetime>
      ),
    },
    {
      key: 'receivedAt',
      title: columnTitle({
        label: (
          <span style={isReceivedAtFilterActive ? { color: RacemapColors.PaleBlue } : undefined}>
            {isReceivedAtFilterActive ? 'Received At (filter)' : 'Received At'}
          </span>
        ),
        headerTitle:
          'When racemap received the read and it\'s delay. Delay means the difference from receiving point in time at racemap to its recording point in time in seconds. Lower is better. Negative differences indicate errors or clocks of the readers being not in sync. If marked with "(filter)", Begin/Finish is applied to this field.',
      }),
      width: 200,
      align: 'center',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => compareDates(a.receivedAt, b.receivedAt),
      render: (_, read) => {
        const receiveTime = DateTime.fromJSDate(read.receivedAt);
        const readTime = DateTime.fromJSDate(read.readAt);
        if (!receiveTime.isValid || !readTime.isValid) return '';

        const diff = receiveTime.diff(readTime).as('seconds');

        return (
          <TooltipWithUTCDatetime datetime={read.receivedAt}>
            <Space>
              <Timestamp time={receiveTime} format="dayWithTime" />
              <div>
                {diff >= 0 ? `(+${diff.toFixed(0)}${HAIRSP}s)` : `(${diff.toFixed(0)}${HAIRSP}s)`}
              </div>
            </Space>
          </TooltipWithUTCDatetime>
        );
      },
    },
    readersColumn(availableReaderIds),
    transponderColumn(availableChipIds),
    {
      key: 'starter',
      title: 'Participant',
      width: 240,
      render: (_, read) => {
        if (read.starter == null) return null;

        return (
          <div style={{ marginLeft: 10 }}>
            <b>{read.starter.startNumber}</b> {read.starter.name}
          </div>
        );
      },
    },
    locationColumn(event?.id),
    userOrCustomerIdColumn(linkedReads),
  ];

  const scrollX = columns.reduce((sum, col) => sum + Number(col.width ?? 150), 0);

  const pageSize = readsFilter.limit ?? 100;
  const currentPage = Math.floor((readsFilter.offset ?? 0) / pageSize) + 1;

  const handleTableChange: TableProps<ReadWithStarter>['onChange'] = (
    pagination,
    filters,
    sorter,
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextPageSize = pagination.pageSize ?? pageSize;
    setReadsFilter({
      offset: ((pagination.current ?? 1) - 1) * nextPageSize,
      limit: nextPageSize,
      timestampSortType:
        singleSorter?.columnKey === 'readAt' ? DBTimestampType.ReadAt : DBTimestampType.ReceivedAt,
      order: singleSorter?.order === 'descend' ? 'DESC' : 'ASC',
      readerIds: (filters.readerId as Array<string>) ?? undefined,
      chipIds: (filters.chipId as Array<string>) ?? undefined,
    });
  };

  return (
    <Container>
      <FetchReadsOptions timeFrames={timeFrames} />

      <TableArea ref={tableAreaRef} emptyBodyHeight={emptyBodyHeight}>
        <Table<ReadWithStarter>
          dataSource={linkedReads as Array<ReadWithStarter>}
          columns={columns}
          rowKey={(read, index) => `${read.id}-${index}`}
          size="small"
          sticky={{ getContainer: () => tableAreaRef.current ?? globalThis.window }}
          scroll={{ x: scrollX }}
          loading={isLoadingReads}
          bordered
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total: readsCount ?? 0,
            disabled: isLoadingReadsCount,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `${total} reads`,
          }}
        />
      </TableArea>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: 10px;
`;

const TableArea = styled.div<{ emptyBodyHeight: number }>`
  flex: 1;
  min-height: 0;
  overflow: auto;

  .ant-table-tbody > tr.ant-table-placeholder > .ant-table-cell {
    height: ${({ emptyBodyHeight }) => emptyBodyHeight}px;
  }

  .ant-table-sticky-holder {
    top: 0 !important;
  }

  .ant-table.ant-table-bordered .ant-table-container {
    border-top: none;
  }

  .ant-table-sticky-holder .ant-table-thead > tr > th {
    border-top: 1px solid #ced4da;
  }

  .ant-table-pagination {
    position: sticky;
    bottom: 0;
    margin: 0 !important;
    padding: 8px 0;
    background: white;
  }
`;

function sourceColumn(): TableColumnType<ReadWithStarter> {
  return {
    key: 'source',
    title: columnTitle({
      label: 'Source',
      hintWidth: 400,
      hint: (
        <>
          <p>
            <IconRacemap style={{ color: RacemapColors.PaleBlue }} />{' '}
            <strong>Timing API read:</strong> Reads sent from any timekeeping solution to RACEMAP
            timing API. More about our timing API{' '}
            <a href="https://docs.racemap.com/prediction/timekeeping/other-timingsystem">
              Documentation
            </a>
            .
          </p>
          <p>
            <IconClock style={{ color: RacemapColors.PaleBlue }} /> <strong>Exporter:</strong> Reads
            are generated through RACE RESULT timing software from detections with Track Boxes,
            Decoders, and Loop Boxes. Each read is associated with a timing point in RACE RESULT.
            Exporter generates not more than one read for each reader and transponder. Timestamp and
            location are determined with high accuracy.
          </p>
          <p>
            <IconLocation style={{ color: RacemapColors.PaleBlue }} /> <strong>Forwarding:</strong>{' '}
            Reads are generated when passing RACE RESULT Track Boxes. The reads are forwarded to
            RACEMAP without being processed in the timing software. In most cases, forwarding
            generates multiple reads for each reader and transponder. Timestamp and location are
            determinfed with less accuracy.
          </p>
        </>
      ),
    }),
    width: 100,
    align: 'center',
    render: (_, read) => <TypeOfSourceIcon source={read.meta?.source} />,
  };
}

function readersColumn(readerIds: Array<string>): TableColumnType<ReadWithStarter> {
  return {
    key: 'readerId',
    title: columnTitle({
      label: 'Reader',
      headerTitle:
        'The optional name and id of the reader that recorded the read. Each reader must have a unique reader id per raceground.',
    }),
    width: 170,
    filters: readerIds.map((readerId) => ({ text: readerId, value: readerId })),
    filterSearch: true,
    render: (_, read) => (
      <TooltipForAnObject object={{ Id: read.readerId, Name: read.readerName }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'left',
            width: '100%',
            marginLeft: '5px',
          }}
        >
          {read.readerName && <b>{read.readerName}</b>}&nbsp;
          {read.readerId}
        </div>
      </TooltipForAnObject>
    ),
  };
}

function transponderColumn(chipIds: Array<string>): TableColumnType<ReadWithStarter> {
  return {
    key: 'chipId',
    title: 'Transponder',
    width: 160,
    align: 'center',
    filters: chipIds.map((chipId) => ({
      text: <TransponderId key={chipId} id={chipId} />,
      value: chipId,
    })),
    filterSearch: (input, filter) =>
      String(filter.value).toLowerCase().includes(input.toLowerCase()),
    render: (_, read) => <TransponderId id={read.chipId ?? null} />,
  };
}

export function locationColumn(eventId?: string): TableColumnType<ReadWithStarter> {
  return {
    key: 'location',
    title: columnTitle({
      label: 'Location',
      headerTitle:
        'The location of the read. If the location is not available, the read was not recorded by a GPS device.',
    }),
    width: 200,
    render: (_, read) => (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {read.lat != null && read.lng != null ? (
          <div
            title={`${
              read.meta?.locationIsUserDefined === true
                ? '!!! This location is defined by a Split in the map editor and therefore prone to human error.'
                : ''
            } Latitude: ${read.lat.toFixed(6)}${HAIRSP} Longitude: ${read.lng.toFixed(6)}${HAIRSP}`}
          >
            <IconLocationDot
              style={{
                color:
                  read.meta?.locationIsUserDefined === true
                    ? RacemapColors.DarkYellow
                    : RacemapColors.PaleBlue,
                position: 'relative',
                top: '0.5px',
                marginLeft: '5px',
                marginRight: '5px',
              }}
            />
            <LatLngExternalLink lat={read.lat} lng={read.lng} />
          </div>
        ) : (
          <>
            <Tag color="error" style={{ display: 'flex', alignItems: 'center', marginLeft: 5 }}>
              <IconUnlink />
            </Tag>
            &nbsp;{' '}
            {read.lat == null && read.lng == null ? (
              <Link to={`/admin/events/${eventId}/map/split`}>
                <span title="If you want to add a location to this unlocated read, you can use our map editor. Just add a timekeeping point to the shadow track. Ensure that the reader ID and the timekeeping ID are exactly the same for manually set locations to work.">
                  No Location
                </span>
              </Link>
            ) : (
              <span title="Your location source could not provide a location for this read. Maybe the GPS device was not having a fix.">
                Location Unknown
              </span>
            )}
          </>
        )}
      </div>
    ),
  };
}

function userOrCustomerIdColumn(linkedReads: PreparedReads): TableColumnType<ReadWithStarter> {
  const userIds = Array.from(new Set(linkedReads.map((r) => r.userId).filter(isDefined)).values());

  return {
    key: 'userId',
    title: columnTitle({
      label: 'User|Cust ID',
      headerTitle:
        'The Racemap id of the user that uploaded this read. If the read was uploaded by a Race Result customer, the customer id is shown instead of the user id.',
    }),
    width: 160,
    filters: userIds.map((userId) => ({ text: userId, value: userId })),
    filterSearch: true,
    onFilter: (value, read) => read.userId === value,
    render: (_, read) => <UserOrCustomerId userOrCustomerId={read.userId} />,
  };
}
